/**
 * Leviathan Web - Driver Form Page
 * Quasar Prime LLC
 * Tactical Industrial Design
 *
 * Create and edit drivers with license, certifications, and compliance tracking
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

import { API_URL } from '@/config/api';
import { readApiFailure } from '@/utils/formErrors';

interface FormData {
  driverNumber: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  employeeId: string;
  hireDate: string;
  licenseNumber: string;
  licenseState: string;
  licenseClass: string;
  licenseExpiry: string;
  hazmatCertified: boolean;
  hazmatExpiry: string;
  tankerEndorsement: boolean;
  twicCard: boolean;
  twicExpiry: string;
  medicalCardExpiry: string;
  notes: string;
  isActive: boolean;
}

const STATES = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
];

const LICENSE_CLASSES = ['A', 'B', 'C', 'CDL-A', 'CDL-B', 'CDL-C'];

export default function DriverFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const isEditMode = Boolean(id);

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState<FormData>({
    driverNumber: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: 'TX',
    zip: '',
    employeeId: '',
    hireDate: '',
    licenseNumber: '',
    licenseState: 'TX',
    licenseClass: 'CDL-A',
    licenseExpiry: '',
    hazmatCertified: false,
    hazmatExpiry: '',
    tankerEndorsement: false,
    twicCard: false,
    twicExpiry: '',
    medicalCardExpiry: '',
    notes: '',
    isActive: true,
  });

  useEffect(() => {
    if (isEditMode) {
      fetchDriver();
    }
  }, [id]);

  const fetchDriver = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/drivers/${id}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        const driver = data.driver || data;
        setFormData({
          driverNumber: driver.driverNumber || '',
          firstName: driver.firstName || '',
          lastName: driver.lastName || '',
          email: driver.email || '',
          phone: driver.phone || '',
          address: driver.address || '',
          city: driver.city || '',
          state: driver.state || 'TX',
          zip: driver.zip || '',
          employeeId: driver.employeeId || '',
          hireDate: driver.hireDate?.split('T')[0] || '',
          licenseNumber: driver.licenseNumber || '',
          licenseState: driver.licenseState || 'TX',
          licenseClass: driver.licenseClass || 'CDL-A',
          licenseExpiry: driver.licenseExpiry?.split('T')[0] || '',
          hazmatCertified: driver.hazmatCertified ?? false,
          hazmatExpiry: driver.hazmatExpiry?.split('T')[0] || '',
          tankerEndorsement: driver.tankerEndorsement ?? false,
          twicCard: driver.twicCard ?? false,
          twicExpiry: driver.twicExpiry?.split('T')[0] || '',
          medicalCardExpiry: driver.medicalCardExpiry?.split('T')[0] || '',
          notes: driver.notes || '',
          isActive: driver.isActive ?? true,
        });
      } else {
        setError('Driver not found');
      }
    } catch (error) {
      setError('Failed to load driver');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      setError('First and last name are required');
      return;
    }

    setIsSaving(true);

    try {
      const url = isEditMode ? `${API_URL}/api/drivers/${id}` : `${API_URL}/api/drivers`;

      const payload = {
        ...formData,
        hireDate: formData.hireDate || null,
        licenseExpiry: formData.licenseExpiry || null,
        hazmatExpiry: formData.hazmatExpiry || null,
        twicExpiry: formData.twicExpiry || null,
        medicalCardExpiry: formData.medicalCardExpiry || null,
      };

      const response = await fetch(url, {
        method: isEditMode ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        navigate('/drivers');
      } else {
        /**
         * The API failure body is { success, error: { code, message, details } }
         * — there is no top-level `message`. `data.message || data.error`
         * therefore put the error OBJECT into React state, and rendering an
         * object as a child made React unmount the whole application to a
         * white page. `readApiFailure` always returns a string, and carries
         * the per-field issues alongside it.
         */
        const failure = await readApiFailure(response, 'Failed to save driver');
        setError(failure.message);
      }
    } catch (error) {
      setError('Failed to save driver');
    } finally {
      setIsSaving(false);
    }
  };

  const updateField = (field: keyof FormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-text-tertiary font-mono animate-pulse">&gt; Loading driver...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative">
        <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber shadow-[0_0_8px_rgba(255,149,0,0.6)]" />
        <div className="border-l-4 border-amber pl-6">
          <div className="flex items-center justify-between">
            <div>
              <h1
                className="text-4xl font-display font-bold text-steel tracking-wide uppercase"
                style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
              >
                {isEditMode ? 'Edit Driver' : 'New Driver'}
              </h1>
              <p className="text-text-secondary text-sm font-mono mt-1">
                {isEditMode ? 'Modify driver details' : 'Add a new driver to the team'}
              </p>
            </div>
            <button
              onClick={() => navigate('/drivers')}
              className="text-text-secondary hover:text-amber font-mono text-sm uppercase tracking-wider transition-colors"
            >
              &larr; Back to Drivers
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-error-red/10 border border-error-red/50 text-error-red p-4 font-mono text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Personal Info */}
        <Panel title="Personal Information">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Driver Number {!isEditMode && '(auto if blank)'}
              </label>
              <input
                type="text"
                value={formData.driverNumber}
                onChange={(e) => updateField('driverNumber', e.target.value)}
                placeholder="DRV-001"
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                First Name *
              </label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => updateField('firstName', e.target.value)}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
                required
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Last Name *
              </label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => updateField('lastName', e.target.value)}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
                required
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Email
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => updateField('email', e.target.value)}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Phone
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => updateField('phone', e.target.value)}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Employee ID
              </label>
              <input
                type="text"
                value={formData.employeeId}
                onChange={(e) => updateField('employeeId', e.target.value)}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Hire Date
              </label>
              <input
                type="date"
                value={formData.hireDate}
                onChange={(e) => updateField('hireDate', e.target.value)}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Address
              </label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => updateField('address', e.target.value)}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                City
              </label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => updateField('city', e.target.value)}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                State
              </label>
              <select
                value={formData.state}
                onChange={(e) => updateField('state', e.target.value)}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              >
                {STATES.map((st) => (
                  <option key={st} value={st}>
                    {st}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                ZIP
              </label>
              <input
                type="text"
                value={formData.zip}
                onChange={(e) => updateField('zip', e.target.value)}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>
          </div>
        </Panel>

        {/* License & Certifications */}
        <Panel title="License & Certifications">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                License Number
              </label>
              <input
                type="text"
                value={formData.licenseNumber}
                onChange={(e) => updateField('licenseNumber', e.target.value)}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                License State
              </label>
              <select
                value={formData.licenseState}
                onChange={(e) => updateField('licenseState', e.target.value)}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              >
                {STATES.map((st) => (
                  <option key={st} value={st}>
                    {st}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                License Class
              </label>
              <select
                value={formData.licenseClass}
                onChange={(e) => updateField('licenseClass', e.target.value)}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              >
                {LICENSE_CLASSES.map((cls) => (
                  <option key={cls} value={cls}>
                    {cls}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                License Expiry
              </label>
              <input
                type="date"
                value={formData.licenseExpiry}
                onChange={(e) => updateField('licenseExpiry', e.target.value)}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Medical Card Expiry
              </label>
              <input
                type="date"
                value={formData.medicalCardExpiry}
                onChange={(e) => updateField('medicalCardExpiry', e.target.value)}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6 pt-6 border-t border-panel-border">
            <div>
              <label className="flex items-center space-x-2 cursor-pointer mb-4">
                <input
                  type="checkbox"
                  checked={formData.hazmatCertified}
                  onChange={(e) => updateField('hazmatCertified', e.target.checked)}
                  className="w-4 h-4 bg-background border border-panel-border text-amber focus:ring-amber"
                />
                <span className="text-text-secondary font-mono text-sm">HAZMAT Certified</span>
              </label>
              {formData.hazmatCertified && (
                <input
                  type="date"
                  value={formData.hazmatExpiry}
                  onChange={(e) => updateField('hazmatExpiry', e.target.value)}
                  placeholder="Expiry Date"
                  className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
                />
              )}
            </div>

            <div>
              <label className="flex items-center space-x-2 cursor-pointer mb-4">
                <input
                  type="checkbox"
                  checked={formData.tankerEndorsement}
                  onChange={(e) => updateField('tankerEndorsement', e.target.checked)}
                  className="w-4 h-4 bg-background border border-panel-border text-amber focus:ring-amber"
                />
                <span className="text-text-secondary font-mono text-sm">Tanker Endorsement</span>
              </label>
            </div>

            <div>
              <label className="flex items-center space-x-2 cursor-pointer mb-4">
                <input
                  type="checkbox"
                  checked={formData.twicCard}
                  onChange={(e) => updateField('twicCard', e.target.checked)}
                  className="w-4 h-4 bg-background border border-panel-border text-amber focus:ring-amber"
                />
                <span className="text-text-secondary font-mono text-sm">TWIC Card</span>
              </label>
              {formData.twicCard && (
                <input
                  type="date"
                  value={formData.twicExpiry}
                  onChange={(e) => updateField('twicExpiry', e.target.value)}
                  placeholder="Expiry Date"
                  className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
                />
              )}
            </div>
          </div>
        </Panel>

        {/* Notes */}
        <Panel title="Notes">
          <textarea
            value={formData.notes}
            onChange={(e) => updateField('notes', e.target.value)}
            rows={3}
            className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
          />

          <div className="mt-4">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => updateField('isActive', e.target.checked)}
                className="w-4 h-4 bg-background border border-panel-border text-amber focus:ring-amber"
              />
              <span className="text-text-secondary font-mono text-sm">
                Active (available for dispatch)
              </span>
            </label>
          </div>
        </Panel>

        {/* Actions */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/drivers')}
            className="bg-panel-border hover:bg-steel/20 text-text-primary font-mono py-3 px-8 transition-all uppercase tracking-wider"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSaving}
            className="bg-amber hover:bg-amber/90 text-background font-display font-bold py-3 px-8 transition-all uppercase tracking-wider disabled:opacity-50"
            style={{ boxShadow: '0 0 20px rgba(255, 149, 0, 0.4)' }}
          >
            {isSaving ? 'Saving...' : isEditMode ? 'Update Driver' : 'Add Driver'}
          </button>
        </div>
      </form>
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="relative stat-card">
      <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
      <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
      <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
      <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

      <div className="bg-panel-dark border border-panel-border p-6 clip-path-octagon">
        <h2 className="text-lg font-display text-text-primary uppercase tracking-wide mb-4 pb-3 border-b border-amber">
          {title}
        </h2>
        {children}
      </div>
    </div>
  );
}
